// A Firestore-dokumentumok értelmezése — tisztán, hálózat nélkül.
//
// Ugyanazt a szerződést követi, mint a Flutter oldal (lib/pages/tasks/models/):
//   - a dátumok ISO 8601 UTC stringek, nem Firestore Timestampek
//   - az enumok a Dart enum NEVÉVEL szerepelnek ("waitingToAccept")
//   - az időmérés másodpercben, tört számként tárolódik
//
// Azért külön fájl, hogy az értelmezés adatbázis nélkül is tesztelhető legyen.

import { t } from './i18n';

export const taskStatuses = [
  'todo',
  'failed',
  'doing',
  'completed',
  'waitingToAccept',
  'unaccepted',
] as const;

export type TaskStatus = (typeof taskStatuses)[number];

export type TaskPriority = 'low' | 'mid' | 'high' | 'urgent';

/// A ds.css osztályai és a SZÓTÁRI KULCSOK státuszonként.
///
/// Kész felirat helyett kulcs áll a táblában: ez a tábla modul szintű, tehát
/// a fájl betöltésekor jön létre — a szótár viszont csak a render előtt
/// érkezik meg. A feliratot a `statusLabel()` adja, hívási időben.
export const statusMeta: Record<TaskStatus, { cls: string; labelKey: string }> = {
  todo: { cls: 'st-todo', labelKey: 'task.toBeDone' },
  waitingToAccept: { cls: 'st-wait', labelKey: 'task.waitingToAccept' },
  unaccepted: { cls: 'st-rej', labelKey: 'task.declined' },
  doing: { cls: 'st-prog', labelKey: 'task.inProgress' },
  completed: { cls: 'st-ok', labelKey: 'task.successfulFulfillment' },
  failed: { cls: 'st-fail', labelKey: 'task.unsuccessfulFulfillment' },
};

export function statusLabel(status: TaskStatus): string {
  return t(statusMeta[status].labelKey);
}

export const priorityMeta: Record<TaskPriority, { cls: string; labelKey: string }> = {
  low: { cls: 'p-low', labelKey: 'task.lowPriority' },
  mid: { cls: 'p-med', labelKey: 'task.midPriority' },
  high: { cls: 'p-high', labelKey: 'task.highPriority' },
  urgent: { cls: 'p-urg', labelKey: 'task.urgentPriority' },
};

export function priorityLabel(priority: TaskPriority): string {
  return t(priorityMeta[priority].labelKey);
}

/// A tárolt prioritás 1..4 egész; ismeretlen érték esetén nincs jelölés.
export function priorityFromValue(value: unknown): TaskPriority | undefined {
  switch (value) {
    case 1:
      return 'low';
    case 2:
      return 'mid';
    case 3:
      return 'high';
    case 4:
      return 'urgent';
    default:
      return undefined;
  }
}

/// A `priorityFromValue` megfordítása: a tárolt egész szám a prioritásból.
///
/// Azért van külön függvény, mert a leképezés eddig KÉT helyen állt a
/// `main.ts`-ben, szó szerint ugyanúgy beírva — egy harmadik hívási helyen
/// könnyen elcsúszott volna.
export function priorityValue(priority: TaskPriority): number {
  return { low: 1, mid: 2, high: 3, urgent: 4 }[priority];
}

export function statusFrom(value: unknown): TaskStatus {
  return taskStatuses.includes(value as TaskStatus) ? (value as TaskStatus) : 'todo';
}

export function dateFrom(value: unknown): Date | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export type TimeTrack = { started?: Date; finished?: Date; duration?: number };

/// Egy megjegyzés a feladaton (`CommentModel`).
export type TaskComment = {
  id?: string;
  text: string;
  authorId: string;
  createdAt?: Date;
  attachmentCount: number;
  /// A megjegyzeshez csatolt kepek/fajlok. A terv (W12) bélyegképként mutatja
  /// őket a buborékban — eddig csak a darabszámuk volt meg.
  attachments: TaskAttachment[];
};

export type TaskAttachment = {
  url?: string;
  kind: 'photo' | 'file';
  /// A feladathoz csatolva (`attachmentsAssignedToTask`), vagy a teljesítéskor
  /// feltöltve (`attachmentsSubmittedByUser`). A kettőt a terv is külön mutatja.
  source: 'assigned' | 'submitted';
  fieldName?: string;
};

/// Egy dinamikus mező a feladaton (`FieldModel`).
export type TaskField = {
  id?: string;
  name: string;
  type: string;
  value?: string;
  /// Típusválasztó mező (`isType`), nem kitöltendő érték.
  isType: boolean;
  /// A részletek fejlécében is látszik (`isOnMainScreen`).
  onMainScreen: boolean;
  /// Az a státusz, amelybe lépéshez a mezőt ki KELL tölteni (`status`).
  /// Hiánya azt jelenti, hogy a mező szabadon hagyható.
  requiredInStatus?: TaskStatus;
};

export type TaskReopen = { at?: Date; reason?: string };

export type TaskView = {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  sequenceNumber?: number;
  priority?: TaskPriority;
  deadline?: Date;
  assigneeIds: string[];
  /// Aki kiosztotta a feladatot.
  submittedUserId?: string;
  /// Aki koveti a feladatot anelkul, hogy felelose lenne.
  followerUserId?: string;
  commentCount: number;
  attachmentCount: number;
  timerRunning: boolean;
  /// A FUTÓ mérés kezdete — a terv (W11) ezt írja ki: „Fut 08:03 óta".
  /// A lezáratlan `timeTracks` bejegyzés kezdete; ha nincs futó mérés, hiányzik.
  timerStartedAt?: Date;
  /// Eltelt idő másodpercben.
  elapsedSeconds: number;
  isOverdue: boolean;
  projectTitle?: string;
  projectId?: string;
  /// A terv „Idővonal" füléhez: a modellben TÉNYLEGESEN tárolt időpontok.
  createdAt?: Date;
  startedAt?: Date;
  finishedAt?: Date;
  reopens: TaskReopen[];
  /// Indoklások: státuszváltás, sikertelenség, elutasítás.
  statusChangeDesc?: string;
  failedDesc?: string;
  declineDesc?: string;
  comments: TaskComment[];
  attachments: TaskAttachment[];
  fields: TaskField[];
  signatureUrl?: string;
  /// Van-e olyan módosítás ezen a feladaton, ami még nem ért fel a
  /// szerverre (4.2). NEM a Firestore adja: az írás a PROJEKT egyetlen
  /// `taskChange` dokumentumát cseréli le, tehát a dokumentum
  /// `hasPendingWrites` jelzése a projekt MINDEN feladatára igaz lenne.
  /// Azt viszont tudjuk, melyik feladatra indítottunk írást — ez a mező
  /// onnan jön, és akkor törlődik, amikor a szerver visszaigazolta.
  pending?: boolean;
};

function commentsOf(task: Raw): TaskComment[] {
  return asArray(task['comments'])
    .map((c) => ({
      id: typeof c['id'] === 'string' ? c['id'] : undefined,
      text: String(c['text'] ?? ''),
      authorId: String(c['submittedUserId'] ?? ''),
      createdAt: dateFrom(c['createdAt']),
      attachmentCount: asArray(c['attachments']).length,
      attachments: asArray(c['attachments']).map((a) => ({
        url: typeof a['url'] === 'string' ? a['url'] : undefined,
        kind: a['type'] === 'photo' ? ('photo' as const) : ('file' as const),
        source: 'submitted' as const,
      })),
    }))
    // Legfrissebb elöl — a terv is így mutatja.
    .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
}

function attachmentsOf(task: Raw): TaskAttachment[] {
  const read = (key: string, source: 'assigned' | 'submitted'): TaskAttachment[] =>
    asArray(task[key]).map((a) => {
      const field = a['field'];
      const name =
        field && typeof field === 'object' && typeof (field as Raw)['name'] === 'string'
          ? ((field as Raw)['name'] as string)
          : undefined;
      return {
        url: typeof a['url'] === 'string' ? a['url'] : undefined,
        kind: a['type'] === 'photo' ? ('photo' as const) : ('file' as const),
        source,
        fieldName: name,
      };
    });

  return [
    ...read('attachmentsAssignedToTask', 'assigned'),
    ...read('attachmentsSubmittedByUser', 'submitted'),
  ];
}

function fieldsOf(task: Raw): TaskField[] {
  return asArray(task['fields']).map((f) => ({
    id: typeof f['id'] === 'string' ? f['id'] : undefined,
    name: String(f['name'] ?? ''),
    type: String(f['type'] ?? 'text'),
    value: typeof f['data'] === 'string' && f['data'].length > 0 ? f['data'] : undefined,
    isType: f['isType'] === true,
    onMainScreen: f['isOnMainScreen'] === true,
    requiredInStatus: taskStatuses.includes(f['status'] as TaskStatus)
      ? (f['status'] as TaskStatus)
      : undefined,
  }));
}

type Raw = Record<string, unknown>;

function asArray(value: unknown): Raw[] {
  return Array.isArray(value) ? (value as Raw[]) : [];
}

/// A feladathoz rendelt emberek azonosítói.
///
/// A modell kétféleképpen tárolja: a régebbi feladatokon egyetlen
/// `assignedUserId`, az újabbakon `assignedUserIds` lista. Mindkettőt nézzük,
/// duplikáció nélkül — ugyanúgy, mint a Flutter oldali illesztő.
export function assigneeIdsOf(task: Raw): string[] {
  const ids = new Set<string>();
  const single = task['assignedUserId'];
  if (typeof single === 'string' && single.length > 0) ids.add(single);
  for (const id of asArray(task['assignedUserIds']) as unknown[]) {
    if (typeof id === 'string' && id.length > 0) ids.add(id);
  }
  return [...ids];
}

export function timeTracksOf(task: Raw): TimeTrack[] {
  return asArray(task['timeTracks']).map((t) => ({
    started: dateFrom(t['started']),
    finished: dateFrom(t['finished']),
    duration: typeof t['duration'] === 'number' ? t['duration'] : undefined,
  }));
}

/// Az eddig mért idő másodpercben.
///
/// A kézi felülírás (`rewriteDuration`) elsőbbséget élvez, ahogy a Flutterben:
/// ha az admin átírta az időt, azt kell mutatni, nem a mért értéket.
export function elapsedSecondsOf(task: Raw, now: Date = new Date()): number {
  const rewrite = task['rewriteDuration'];
  if (typeof rewrite === 'number' && rewrite !== 0) return rewrite;

  return timeTracksOf(task).reduce((sum, track) => {
    if (typeof track.duration === 'number') return sum + track.duration;
    // Nyitott szakasz: a kezdés óta eltelt idő.
    if (track.started) return sum + (now.getTime() - track.started.getTime()) / 1000;
    return sum;
  }, 0);
}

export function isTimerRunning(task: Raw): boolean {
  return timeTracksOf(task).some((t) => t.started !== undefined && t.duration === undefined);
}

export function toTaskView(
  task: Raw,
  context: { projectTitle?: string; projectId?: string; now?: Date } = {},
): TaskView {
  const now = context.now ?? new Date();
  const status = statusFrom(task['status']);
  const deadline = dateFrom(task['deadline']);
  const order = task['order'];

  const comments = commentsOf(task);
  const attachments = attachmentsOf(task);
  const signature = task['signature'];

  return {
    id: String(task['id'] ?? ''),
    title: String(task['title'] ?? ''),
    description: String(task['desc'] ?? ''),
    status,
    // A tárolt sorrend nullától indul, a felhasználónak egytől számozunk.
    sequenceNumber: typeof order === 'number' ? order + 1 : undefined,
    priority: priorityFromValue(task['priority']),
    deadline,
    assigneeIds: assigneeIdsOf(task),
    submittedUserId:
      typeof task['submittedUserId'] === 'string' ? task['submittedUserId'] : undefined,
    followerUserId:
      typeof task['followerUserId'] === 'string' ? task['followerUserId'] : undefined,
    commentCount: comments.length,
    attachmentCount: attachments.length,
    timerRunning: isTimerRunning(task),
    timerStartedAt: timeTracksOf(task).find(
      (track) => track.started !== undefined && track.finished === undefined,
    )?.started,
    elapsedSeconds: elapsedSecondsOf(task, now),
    // Lezárt feladatnál már nincs értelme lejártnak jelölni.
    isOverdue:
      deadline !== undefined &&
      deadline.getTime() < now.getTime() &&
      status !== 'completed' &&
      status !== 'failed',
    projectTitle: context.projectTitle,
    projectId: context.projectId,
    createdAt: dateFrom(task['createdAt']),
    startedAt: dateFrom(task['started']),
    finishedAt: dateFrom(task['finished']),
    reopens: asArray(task['reopens']).map((r) => ({
      at: dateFrom(r['createdAt']),
      // A modell mezoneve `reopenDesc`; a webes iras egy ideig `desc`-kent
      // mentette, ezert a mar letezo sorok miatt mindkettot olvassuk.
      reason:
        typeof r['reopenDesc'] === 'string'
          ? r['reopenDesc']
          : typeof r['desc'] === 'string'
            ? r['desc']
            : undefined,
    })),
    statusChangeDesc:
      typeof task['statusChangeDesc'] === 'string' ? task['statusChangeDesc'] : undefined,
    failedDesc: typeof task['failedDesc'] === 'string' ? task['failedDesc'] : undefined,
    declineDesc: typeof task['declineDesc'] === 'string' ? task['declineDesc'] : undefined,
    comments,
    attachments,
    fields: fieldsOf(task),
    signatureUrl:
      signature && typeof signature === 'object' && typeof (signature as Raw)['url'] === 'string'
        ? ((signature as Raw)['url'] as string)
        : undefined,
  };
}

/// Egy projekt (taskChange dokumentum) feladatai, sorrendben.
export function projectTasks(taskChange: Raw, now?: Date): TaskView[] {
  const title = typeof taskChange['title'] === 'string' ? taskChange['title'] : undefined;
  const projectId =
    typeof taskChange['projectId'] === 'string' ? taskChange['projectId'] : undefined;

  return asArray(taskChange['tasks'])
    .map((task) => toTaskView(task, { projectTitle: title, projectId, now }))
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
}

export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0
    ? t('task.elapsedHoursMinutes', [String(hours), String(minutes)])
    : t('task.elapsedMinutes', [String(minutes)]);
}

/// Óra alakú időtartam („02:41:08") — a terv ezt mutatja a felülírásnál.
///
/// A `formatElapsed` rövid alakja („2 ó 41 p") a listában jó, de a felülíráskor
/// a másodpercek is számítanak: abból derül ki, hogy a mérés épp fut.
export function formatElapsedClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/// Óra:perc egy időpontból („08:03").
export function formatClock(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatDeadline(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}. ${pad(date.getMonth() + 1)}. ${pad(date.getDate())}. ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/// Rövid határidő a listasorhoz: „Ma 16:00", „Holnap 09:00", „Tegnap 12:30",
/// különben „09. 27. 12:31" (a folyó év kimarad).
///
/// A terven a listában ez az alak áll, nem a teljes dátum: a 128 px-es oszlop
/// a teljes alaktól kifolyna, és a lényeg — hogy MA jár le — épp elveszne.
export function formatDeadlineShort(date: Date, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((day.getTime() - today.getTime()) / 86400000);

  if (days === 0) return `Ma ${time}`;
  if (days === 1) return `Holnap ${time}`;
  if (days === -1) return `Tegnap ${time}`;
  if (date.getFullYear() === now.getFullYear()) {
    return `${pad(date.getMonth() + 1)}. ${pad(date.getDate())}. ${time}`;
  }
  return formatDeadline(date);
}
